/**
 * AddTaskDialog.tsx
 * FEAT-2026-0007 — "+ Add Task" on the Phase-1 scenario task table.
 *
 * Lets the planner add a single new task by picking a TaskType (test type).
 * Creates a manual ScheduleTask on the scenario's source (unplaced); the user
 * assigns a machine afterwards. Replaces the old "+ Add Task = re-generate from
 * source" behaviour, which added nothing when the source had no new line items.
 */
import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { fetchTaskTypes } from '../../../../services/scheduleTaxonomy';
import { createScheduleTask } from '../../../../services/scheduleTasks';
import { continuousSpanDays } from '../../../../utils/runMode';
import { isContinuousRun, CONTINUOUS_HINT } from '../../../../utils/runModeRendering';
import type { TaskType } from '../../../../types/schedule';

interface AddTaskDialogProps {
  open: boolean;
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  onClose: () => void;
  /** Called after a task is created so the parent can refetch the table + grid. */
  onCreated: () => void;
}

export const AddTaskDialog: React.FC<AddTaskDialogProps> = ({
  open,
  source,
  sourceKey,
  onClose,
  onCreated,
}) => {
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedType, setSelectedType] = useState<TaskType | null>(null);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState('1');
  const [machineQty, setMachineQty] = useState('1');

  // Load task types when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchTaskTypes()
      .then((tt) => { if (active) setTaskTypes(tt.filter((t) => t.IsActive)); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load task types.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  // Reset fields each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelectedType(null);
      setName('');
      setDuration('1');
      setMachineQty('1');
      setError(null);
    }
  }, [open]);

  const handleSelectType = (t: TaskType | null) => {
    setSelectedType(t);
    if (t) {
      setName(t.Name);
      // RM-17: a CONTINUOUS type's duration is CALENDAR days. When the type
      // carries a resolvable DefaultMinutes rate, default the span from it
      // (continuousSpanDays — one test, 24/7); otherwise keep the taxonomy
      // preset. Attended types keep the historical preset default.
      const continuousDefault =
        isContinuousRun(t.RunMode) && t.DefaultMinutes
          ? continuousSpanDays(t.DefaultMinutes)
          : null;
      setDuration(String(continuousDefault ?? Math.max(1, t.PresetDurationDays || 1)));
      setMachineQty(String(Math.max(1, t.DefaultMachineCount || 1)));
    }
  };

  const isContinuousType = isContinuousRun(selectedType?.RunMode);
  const canSave = !!selectedType && name.trim().length > 0 && !saving;

  const handleCreate = async () => {
    if (!selectedType) return;
    setSaving(true);
    setError(null);
    try {
      await createScheduleTask({
        name: name.trim(),
        taskTypeKey: selectedType.TaskTypeKey,
        source,
        sourceKey,
        presetDurationDays: Math.max(1, parseInt(duration, 10) || 1),
        machineCount: Math.max(1, parseInt(machineQty, 10) || 1),
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add task.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Task</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={taskTypes}
            loading={loading}
            value={selectedType}
            onChange={(_e, v) => handleSelectType(v)}
            getOptionLabel={(o) => o.Name}
            isOptionEqualToValue={(a, b) => a.TaskTypeKey === b.TaskTypeKey}
            groupBy={(o) => o.LabSection ?? o.Category ?? 'Other'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Task type"
                required
                placeholder="Search test / task types…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Task name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={isContinuousType ? 'Duration (calendar days)' : 'Duration (days)'}
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Machine Qty"
              type="number"
              value={machineQty}
              onChange={(e) => setMachineQty(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ flex: 1 }}
            />
          </Box>
          {/* RM-17: make the 24/7 semantics visible before the task is created. */}
          {isContinuousType && (
            <Typography variant="caption" color="text.secondary">
              {CONTINUOUS_HINT}.
            </Typography>
          )}
          {error && (
            <Typography variant="caption" color="error">{error}</Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            The task is added unplaced — pick a machine for it in the table afterwards.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={() => { void handleCreate(); }}
          disabled={!canSave}
          variant="contained"
          sx={{ textTransform: 'none' }}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Add Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};
